import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { AddProgramDto } from "../models/program/addProgramDto";
import { GenericResponseDto } from "../models/generic-response.dto";
import { DropDownDto } from "../models/program/dropdownDto";
import { ProgramListDto } from "../models/program/programListDto";
import { ProgramClientMapDto } from "../models/program/programClientMap";
import { ClientListDto } from "../models/program/clientListDto";
import { ProgramBCDto } from "../models/program/programBCListDto";
import { ProgramDimensionDto } from "../models/orders/programDimensionDto";
import { GetProgramDetailsEmailDto } from "../models/client/addclient/getProgramDetailsForEmailDto";
import { ProgramClientListDto } from "../models/program/programClientListDto";
import { DefaultClientProgramMapRequest } from "../models/program/defaultClientProgramMapRequest";
import { GetD365DefaultProgramsAndSalesClassificationByClientResponse } from "../models/program/getD365DefaultProgramsAndSalesClassificationByClientResponse";
import { GetUserDto } from "../models/users/users/getUserDto";
import { ApiCacheService } from "./api-cache.service";

@Injectable({
  providedIn: "root",
})
export class ProgramsService {
  private userManagementBaseURL = environment.baseUrl + "/User";
  private programBaseURL = environment.baseUrl + "/Program";
  private readonly getAllSalesClassificationsUrl =
    this.programBaseURL + "/GetAllSalesClassifications";
  private readonly salesClassificationsTtl = 60 * 60 * 1000; // 1 hour

  constructor(
    private _http: HttpClient,
    private _apiCache: ApiCacheService
  ) {}

  createProgram(
    request: AddProgramDto
  ): Observable<GenericResponseDto<AddProgramDto>> {
    return this._http.post<GenericResponseDto<AddProgramDto>>(
      this.programBaseURL + `/AddProgram`,
      request
    );
  }

  getBCList(): Observable<GenericResponseDto<DropDownDto[]>> {
    return this._http.get<GenericResponseDto<DropDownDto[]>>(
      this.programBaseURL + "/GetAllPlatformList"
    );
  }

  getPlatformList(): Observable<GenericResponseDto<DropDownDto[]>> {
    return this._http.get<GenericResponseDto<DropDownDto[]>>(
      this.programBaseURL + "/GetAllPlatformList"
    );
  }

  getFacilitatorList(): Observable<GenericResponseDto<DropDownDto[]>> {
    return this._http.get<GenericResponseDto<DropDownDto[]>>(
      this.programBaseURL + "/GetAllFacilitatorList"
    );
  }

  getThreePLList(): Observable<GenericResponseDto<DropDownDto[]>> {
    return this._http.get<GenericResponseDto<DropDownDto[]>>(
      this.programBaseURL + "/GetAll3PLList"
    );
  }

  getAllProgramList(
    showClient: boolean
  ): Observable<GenericResponseDto<ProgramListDto[]>> {
    return this._http.get<GenericResponseDto<ProgramListDto[]>>(
      `${this.programBaseURL}/GetAllPrograms?showClient=${String(showClient)}`
    );
  }

  getProgarmDetailsById(
    programId: number
  ): Observable<GenericResponseDto<AddProgramDto>> {
    return this._http.get<GenericResponseDto<AddProgramDto>>(
      `${this.programBaseURL}/GetProgramDetails?programId=${String(programId)}`
    );
  }

  createProgramClientRelation(
    mapper: ProgramClientMapDto
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.programBaseURL + "/CreateProgramClientMap",
      mapper
    );
  }

  getClientList(
    programId: number
  ): Observable<GenericResponseDto<ClientListDto[]>> {
    return this._http.get<GenericResponseDto<ClientListDto[]>>(
      `${this.programBaseURL}/GetAllClientsByProgram?programId=${String(programId)}`
    );
  }

  getWhoISupport(): Observable<GenericResponseDto<ProgramBCDto[]>> {
    return this._http.get<GenericResponseDto<ProgramBCDto[]>>(
      this.programBaseURL + "/GetAllWhoISupport"
    );
  }

  getProgramDimensions(): Observable<
    GenericResponseDto<ProgramDimensionDto[]>
  > {
    return this._http.get<GenericResponseDto<ProgramDimensionDto[]>>(
      this.programBaseURL + "/GetProgramDimensionsList"
    );
  }

  GetMyProfile(): Observable<GenericResponseDto<GetUserDto>> {
    return this._http.get<GenericResponseDto<GetUserDto>>(
      this.userManagementBaseURL + "/GetMyProfile"
    );
  }

  GetProgramEmailDetails(
    programId = "",
    customerAccount = ""
  ): Observable<GenericResponseDto<GetProgramDetailsEmailDto>> {
    return this._http.get<GenericResponseDto<GetProgramDetailsEmailDto>>(
      `${this.programBaseURL}/GetProgramEmailDetails?ProgramId=${programId}&CustomerAccount=${customerAccount}`
    );
  }

  RemoveProgramClientRelation(
    mapperList: ProgramClientMapDto[]
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.programBaseURL + "/RemoveProgramClientMap",
      mapperList
    );
  }

  ProgramApprove(
    programId: number,
    isApproved: boolean
  ): Observable<GenericResponseDto<AddProgramDto>> {
    return this._http.post<GenericResponseDto<AddProgramDto>>(
      this.programBaseURL + "/ApproveProgram",
      { programId: programId, isApproved: isApproved }
    );
  }

  UpdateProgram(
    request: AddProgramDto
  ): Observable<GenericResponseDto<AddProgramDto>> {
    return this._http.post<GenericResponseDto<AddProgramDto>>(
      this.programBaseURL + `/UpdateProgram`,
      request
    );
  }

  getProgramByNameFilter(
    programName = "",
    moduleName = ""
  ): Observable<GenericResponseDto<ProgramListDto[]>> {
    return this._http.get<GenericResponseDto<ProgramListDto[]>>(
      this.programBaseURL +
        `/SearchProgramByName?programName=${encodeURIComponent(
          programName
        )}&moduleName=${moduleName}`
    );
  }

  getAllSalesClassifications(): Observable<GenericResponseDto<DropDownDto[]>> {
    return this._apiCache.get<GenericResponseDto<DropDownDto[]>>(
      this.getAllSalesClassificationsUrl,
      this.salesClassificationsTtl
    );
  }

  getProgramByNameFilterForSalesOrder(
    programName = "",
    customerAccount = ""
  ): Observable<GenericResponseDto<ProgramClientListDto[]>> {
    return this._http.post<GenericResponseDto<ProgramClientListDto[]>>(
      this.programBaseURL + `/SearchProgramsByNameForSalesOrder`,
      { programName: programName, customerAccount: customerAccount }
    );
  }

  defaultClientProgramMap(
    request: DefaultClientProgramMapRequest
  ): Observable<GenericResponseDto<boolean>> {
    return this._http.post<GenericResponseDto<boolean>>(
      this.programBaseURL + "/DefaultClientProgramMap",
      request
    );
  }

  getD365DefaultProgramsAndSalesClassificationByClient(
    clientId: string
  ): Observable<
    GenericResponseDto<GetD365DefaultProgramsAndSalesClassificationByClientResponse>
  > {
    return this._http.get<
      GenericResponseDto<GetD365DefaultProgramsAndSalesClassificationByClientResponse>
    >(
      `${this.programBaseURL}/GetD365DefaultProgramsAndSalesClassificationByClient?clientId=${clientId}`
    );
  }
}
